import { useCallback, useEffect, useState } from 'react'
import {
  byUrgency,
  completeTask,
  loadLetters,
  markNotDone,
  type LetterTask,
} from '@core/letters'
import { loadFamily, type FamilyMember, type FamilySession } from '@core/session'
import { moveToTrash } from '@core/trash'
import { Card, Screen, Section, Spinner } from '@design/components'
import { TodoForm } from './TodoForm'
import { TodoList } from './TodoList'
import { TodoSheet } from './TodoSheet'
import { QuickAdd } from './QuickAdd'

/**
 * Te doen — the household list, kept apart from the letters.
 *
 * They share a table because they are the same shape, and they share the same
 * closure loop: something needs doing, someone does it, whoever asked is told.
 * They do not share a list, because "de brief van de gemeente" and "lampen
 * kopen" are answered in completely different frames of mind.
 *
 * Assigned tasks come first and say whose they are. A list where you cannot
 * see at a glance what is yours is a list you stop reading.
 */
export function TodosTab({ session }: { session: FamilySession }) {
  const [tasks, setTasks] = useState<LetterTask[] | null>(null)
  const [family, setFamily] = useState<FamilyMember[]>([])
  const [adding, setAdding] = useState(false)
  const [opened, setOpen_] = useState<LetterTask | null>(null)
  const [showDone, setShowDone] = useState(false)

  const refresh = useCallback(() => {
    loadLetters('todo').then(setTasks).catch(() => setTasks([]))
  }, [])

  useEffect(refresh, [refresh])
  useEffect(() => {
    loadFamily(session.accountId)
      .then(setFamily)
      .catch(() => setFamily([]))
  }, [session.accountId])

  const nameOf = (userId: string | null) =>
    userId ? (family.find((m) => m.userId === userId)?.displayName ?? 'Iemand') : null

  if (!tasks) return <Spinner label="Even geduld…" />

  const open = tasks.filter((t) => !t.doneAt).sort(byUrgency)
  const mine = open.filter((t) => !t.assignedTo || t.assignedTo === session.userId)
  const theirs = open.filter((t) => t.assignedTo && t.assignedTo !== session.userId)
  // Most recently finished first. `loadLetters` orders by when a task was
  // *made*, which for a done list is the wrong question — what you want to see
  // at the top is what you just ticked, so an accidental tap is one tap back.
  const done = tasks
    .filter((t) => t.doneAt)
    .sort((a, b) => (b.doneAt ?? '').localeCompare(a.doneAt ?? ''))
    .slice(0, 15)

  // Who a task can be handed to: everyone in the family except yourself.
  const others = family.filter((m) => m.userId !== session.userId)

  return (
    <>
      <Screen>
        {open.length === 0 && (
          <Card className="text-center py-8 mb-3">
            <p className="text-lg font-medium mb-1">Niets te doen</p>
            <p className="text-muted text-sm">Voeg iets toe als er iets moet gebeuren.</p>
          </Card>
        )}

        <TodoList
          title="Voor jou"
          tasks={mine}
          nameOf={nameOf}
          onDone={async (task) => {
            await completeTask(task, session.userId)
            refresh()
          }}
          onOpen={setOpen_}
        />

        <TodoList
          title="Bij iemand anders"
          tasks={theirs}
          nameOf={nameOf}
          onDone={async (task) => {
            await completeTask(task, session.userId)
            refresh()
          }}
          onOpen={setOpen_}
        />

        {done.length > 0 && (
          <Section title="Gedaan" count={done.length}>
            {!showDone ? (
              <button
                onClick={() => setShowDone(true)}
                className="w-full py-2 text-sm text-muted"
              >
                Laat zien
              </button>
            ) : (
              <div className="space-y-2">
                {done.map((task) => (
                  <Card key={task.id} className="flex items-center gap-3 opacity-60">
                    <span className="flex-1 min-w-0 truncate line-through">{task.title}</span>
                    <button
                      onClick={() => {
                        // Optimism is wrong here: the row disappears from
                        // "klaar" only once the write actually landed. An
                        // unhandled rejection made this tap do nothing at all.
                        markNotDone(task.id).then(refresh, () => refresh())
                      }}
                      className="shrink-0 text-sm underline underline-offset-2"
                    >
                      Terug
                    </button>
                  </Card>
                ))}
              </div>
            )}
          </Section>
        )}

      </Screen>

      {/* Outside the scrolling area on purpose, and now a field rather than a
          button. A button opened a form; a field is where you type. That is the
          difference between a list that gets used and one that gets a scrap of
          paper instead. */}
      <QuickAdd session={session} onAdded={refresh} onMore={() => setAdding(true)} />

      {opened && (
        <TodoSheet
          task={opened}
          family={others}
          onClose={() => setOpen_(null)}
          onChanged={refresh}
          onRemove={async () => {
            await moveToTrash('task', opened.id)
            refresh()
          }}
        />
      )}

      {adding && (
        <TodoForm
          session={session}
          onDone={() => {
            setAdding(false)
            refresh()
          }}
          onCancel={() => setAdding(false)}
        />
      )}
    </>
  )
}
